import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { cmsFetch } from "@jambushrushti/services/adminAuth";

const API_BASE = import.meta.env.DEV
  ? "/jambushrusti-api"
  : (
      import.meta.env.VITE_API_BASE_URL ||
      "https://api.jambushusti.universal-software.in/api"
    ).replace(/\/$/, "");

const CONTACT_ENDPOINT = `${API_BASE}/jambushrusti/cms/contact-us/`;

export interface ContactUsSubmission {
  id: string;
  name: string;
  email: string;
  inquiry_type: string;
  subject: string;
  message: string;
  status?: string;
  created_at: string;
}

// Fetch all contact us submissions
export const useContactUsSubmissions = () => {
  return useQuery({
    queryKey: ["contact-us"],
    queryFn: async (): Promise<ContactUsSubmission[]> => {
      const response = await cmsFetch(CONTACT_ENDPOINT);

      if (!response.ok) {
        throw new Error("Failed to fetch contact us submissions");
      }

      const data = await response.json();
      return data.results || data.data || data;
    },
  });
};

// Fetch a single contact us submission
export const useContactUsSubmission = (id: string) => {
  return useQuery({
    queryKey: ["contact-us", id],
    queryFn: async (): Promise<ContactUsSubmission> => {
      const response = await cmsFetch(`${CONTACT_ENDPOINT}${id}/`);

      if (!response.ok) {
        throw new Error("Failed to fetch contact us submission");
      }

      return response.json();
    },
    enabled: !!id,
  });
};

// Update contact us submission (e.g., status)
export const useUpdateContactUs = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      ...data
    }: Partial<ContactUsSubmission> & { id: string }) => {
      const response = await cmsFetch(`${CONTACT_ENDPOINT}${id}/`, {
        method: "PATCH",
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error("Failed to update contact us submission");
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contact-us"] });
    },
  });
};

// Delete contact us submission
export const useDeleteContactUs = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await cmsFetch(`${CONTACT_ENDPOINT}${id}/`, {
        method: "DELETE",
      });

      if (!response.ok) {
        throw new Error("Failed to delete contact us submission");
      }
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contact-us"] });
    },
  });
};
